import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePaymentIntent,
  CreateRefund,
  FeeComposition,
  ListPaymentsQuery,
  Payment,
  PaymentIntentCreated,
  PaymentMethod,
  PaymentStatus,
  Refund,
} from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS-MONEY — taking money at the till over the typed client, mirroring data/vouchers.ts. Three
 * surfaces back a checkout: create a PaymentIntent for the basket, capture an in-person Terminal
 * intent (server-driven manual capture), and a transparent fee preview (Overview §6.10).
 *
 * HONESTY (non-negotiable): every figure shown is the SERVER's. The fee preview is the API's fee
 * composition (platform + Stripe), never recomputed locally; the payment amount, status and splits
 * come back from the server and the UI only DISPLAYS them. Intent creation and capture are IDEMPOTENT
 * on a client-supplied `idempotencyKey` minted ONCE per intent — the caller mints the key at the start
 * of the take and gates the button on `isPending`, so a double-tap is a safe no-op (a replay returns
 * the original payment and moves no money). 422/409 surface faithfully through the shared
 * ApiClientError envelope.
 *
 * Own cache namespace local to this file (money is never cached aggressively / never persisted to the
 * offline replica) — reads here are point-in-time and re-fetched live.
 */
const pk = {
  feePreview: (amountPence: number, method: PaymentMethod) =>
    ['payments', 'fee-preview', amountPence, method] as const,
};

export const PAYMENTS_PAGE = 50;

/** The takings-ledger filter the back-office passes (a thin shape over ListPaymentsQuery). */
export interface PaymentsFilter {
  status?: PaymentStatus;
  appointmentId?: string;
  from?: string;
  to?: string;
  offset?: number;
}

export interface PaymentPage {
  data: Payment[];
  pagination: { limit: number; offset: number; total: number };
}

// ── Reads ──────────────────────────────────────────────────────────────────────────

/**
 * Transparent fee preview for an amount (Overview §6.10): the platform + Stripe split and the net to
 * the salon. Read-only; the till shows this so the salon sees exactly what a charge costs BEFORE
 * taking it. Gated on a positive amount so an empty basket doesn't fire the query; read live
 * (staleTime 0) because the fee config is a salon setting the preview must reflect immediately.
 */
export function useFeePreview(
  amountPence: number,
  method: PaymentMethod = 'online',
  enabled = true,
) {
  return useQuery({
    queryKey: pk.feePreview(amountPence, method),
    enabled: enabled && amountPence > 0,
    staleTime: 0,
    queryFn: async (): Promise<FeeComposition> =>
      unwrap(
        await api.GET('/v1/payments/fee-preview', {
          params: { query: { amountPence, method } },
        }),
      ),
  });
}

/**
 * The takings LEDGER (WS6 back-office): a paginated, filterable list of payments. RLS scopes it on the
 * server (a renter sees their own takings; owner/manager see the floor). MONEY HONESTY: `staleTime:0`
 * so the refundable/refunded figures are always live, and this key is NOT persisted — money is never
 * served from the offline replica. The whole filter object keys the cache so a window/status change
 * caches independently; `keepPreviousData` keeps the table from flashing while paging.
 */
export function usePayments(filter: PaymentsFilter = {}) {
  const offset = filter.offset ?? 0;
  const query = {
    status: filter.status,
    appointmentId: filter.appointmentId,
    from: filter.from,
    to: filter.to,
    limit: PAYMENTS_PAGE,
    offset,
  };
  return useQuery({
    queryKey: qk.payments(query),
    staleTime: 0,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PaymentPage> =>
      unwrap(
        await api.GET('/v1/payments', {
          params: { query: query as ListPaymentsQuery },
        }),
      ),
  });
}

/**
 * One payment with its splits (the ledger detail / refund context). MONEY HONESTY: `staleTime:0` so the
 * remaining-refundable figure a refund reads (`amountPence − amountRefundedPence`) is always the
 * server's live view; the key is not persisted.
 */
export function usePayment(id: string | null) {
  return useQuery({
    queryKey: id ? qk.payment(id) : ['payment', 'none'],
    enabled: Boolean(id),
    staleTime: 0,
    queryFn: async (): Promise<Payment> =>
      unwrap(await api.GET('/v1/payments/{id}', { params: { path: { id: id! } } })),
  });
}

// ── Mutations (idempotent on a per-intent key; gate the button on `isPending`) ───────

/**
 * Create a PaymentIntent (online or Terminal) for a charge, returning the persisted payment +
 * `clientSecret` to confirm client-side. The caller mints `idempotencyKey` ONCE per take and passes
 * it in the body: a re-delivered create returns the ORIGINAL intent (no second charge). Returns the
 * full PaymentIntentCreated so the till can record the payment id (needed to issue the receipt) and
 * confirm the client_secret with Stripe.js / Terminal.
 */
export function useCreatePaymentIntent() {
  return useMutation({
    mutationFn: async (body: CreatePaymentIntent): Promise<PaymentIntentCreated> =>
      unwrap(await api.POST('/v1/payments/intents', { body })),
  });
}

/**
 * Capture an in-person Terminal PaymentIntent (server-driven manual capture). Idempotent on
 * `idempotencyKey`; returns the updated Payment with its (server-derived) status — `succeeded` once
 * the reader confirms. Online intents auto-capture and never need this; only the Terminal leg does.
 */
export function useCapturePayment() {
  return useMutation({
    mutationFn: async (input: { id: string; idempotencyKey: string }): Promise<Payment> =>
      unwrap(
        await api.POST('/v1/payments/{id}/capture', {
          params: { path: { id: input.id } },
          body: { idempotencyKey: input.idempotencyKey },
        }),
      ),
  });
}

/**
 * Refund a payment, FULL (omit `amountPence`) or PARTIAL (pass it). Manager-only on the API. IDEMPOTENT
 * on a per-payment `idempotencyKey` the CALLER mints ONCE per intent (a re-delivered refund replays the
 * original and moves no extra money; the same key on a DIFFERENT payment is a 409 key_conflict, so never
 * reuse it). Only allowed when the payment's status is `succeeded` or `partially_refunded` (else 409) —
 * the UI gates the action off otherwise so a guaranteed-409 is never fired.
 *
 * HONESTY: the refund amount/remaining is the server's (`amountPence − amountRefundedPence` from the
 * payment DTO); the UI never recomputes a refundable figure. On success we drop the ledger + this
 * payment's detail cache so the new `amountRefundedPence`/`status` are re-read live.
 */
export function useCreateRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      paymentId: string;
      amountPence?: number;
      reason: CreateRefund['reason'];
      idempotencyKey: string;
    }): Promise<Refund> =>
      unwrap(
        await api.POST('/v1/payments/{id}/refunds', {
          params: { path: { id: input.paymentId } },
          // The generated body type over-requires (reason has a server default); the server re-validates
          // the same Zod, so cast to `never` and let it own validation.
          body: {
            amountPence: input.amountPence,
            reason: input.reason,
            idempotencyKey: input.idempotencyKey,
          } as never,
        }),
      ),
    onSuccess: (_refund, input) => {
      void qc.invalidateQueries({ queryKey: qk.payment(input.paymentId) });
      // Drop the whole ledger list subtree — any filtered page may now show a new refunded figure/status.
      void qc.invalidateQueries({ queryKey: ['payments', 'list'] });
    },
  });
}

/**
 * Charge a NO-SHOW FEE for an appointment the floor has marked `no_show` (manager-only on the API). This
 * is the DEPOSIT-FORFEIT / no-show path, distinct from refunding a deposit: it MINTS a new charge, it
 * does NOT move an existing payment. REQUIRES `appointment.status === 'no_show'` (else 409 `not_no_show`),
 * so the UI gates on that and shows an honest note. IDEMPOTENT on a per-intent `idempotencyKey` the
 * caller mints ONCE.
 *
 * HONESTY: the policy engine only SUGGESTS an amount; the manager confirms `amountPence` and the SERVER
 * is authoritative for what is charged. On success we drop the ledger so the new fee row appears live.
 */
export function useNoShowFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointmentId: string;
      amountPence?: number;
      idempotencyKey: string;
    }): Promise<PaymentIntentCreated> =>
      unwrap(
        await api.POST('/v1/payments/no-show-fee', {
          // The generated body type over-requires; the server re-validates the same Zod (CreateNoShowFee).
          body: {
            appointmentId: input.appointmentId,
            amountPence: input.amountPence,
            idempotencyKey: input.idempotencyKey,
          } as never,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payments', 'list'] });
    },
  });
}
